import React, { useState, useEffect } from "react";
import {
  Title,
  Text,
  Card,
  Loader,
  Center,
  Alert,
  Button,
  Group,
  Stack,
  Badge,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconRefresh,
  IconArrowLeft,
  IconKey,
} from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { getAdminApiEndpoint, setAdminCompanyCode } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { useAuth } from "../../context/AuthContext";
import PageContent from "../shared/PageContent/PageContent";
import ResetPasswordModal from "./ResetPasswordModal";

const CustomerSupportPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);

  // Check if user has access (REPPI_ADMIN only)
  useEffect(() => {
    if (!user || user.role !== "REPPI_ADMIN") {
      navigate("/dashboard");
      return;
    }

    if (code) {
      // Keep the admin tools that aren't opened from a company page pointed at
      // the company being worked on.
      setAdminCompanyCode(code);
      fetchClientDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, user]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/client/${code}`),
        {
          method: "GET",
          headers: adminHeaders(jwt, code),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch client details: ${response.status}`);
      }

      const result = await response.json();
      setClient(result.data || result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(`/client/${code}`);
  };

  if (!user || user.role !== "REPPI_ADMIN") {
    return (
      <PageContent
        title="Access Denied"
        subtitle="You do not have permission to access this page"
      >
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Denied"
          color="red"
        >
          Only REPPI admins can access customer support pages.
        </Alert>
      </PageContent>
    );
  }

  if (loading) {
    return (
      <PageContent
        title="Customer Support"
        subtitle="Loading support information..."
      >
        <Center style={{ height: 400 }}>
          <Stack align="center">
            <Loader size="lg" />
            <Text>Loading support information...</Text>
          </Stack>
        </Center>
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent
        title="Customer Support"
        subtitle="Error loading support information"
      >
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error loading support information"
          color="red"
          withCloseButton={false}
        >
          <Text mb="md">{error}</Text>
          <Group gap="sm">
            <Button
              leftSection={<IconRefresh size={14} />}
              onClick={fetchClientDetails}
              color="red"
              variant="outline"
            >
              Retry
            </Button>
            <Button
              leftSection={<IconArrowLeft size={14} />}
              onClick={handleBackClick}
              variant="outline"
            >
              Back to Client
            </Button>
          </Group>
        </Alert>
      </PageContent>
    );
  }

  return (
    <PageContent
      title={`Customer Support - ${client?.name || "Client"}`}
      subtitle="Manage client assistance and view company users"
    >
      <Group mb="lg">
        <Button
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBackClick}
          variant="outline"
        >
          Back to Client Details
        </Button>
      </Group>

      <Stack gap="lg">
        {/* Client Overview Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={2}>Client Overview</Title>
            <Group gap="sm">
              <Button
                leftSection={<IconKey size={16} />}
                onClick={() => setIsResetPasswordModalOpen(true)}
                color="red"
                variant="light"
                size="sm"
              >
                Reset User Password
              </Button>
              <Badge color="blue" variant="filled">
                {client?.company_admins?.length || 0} Admin
                {client?.company_admins?.length !== 1 ? "s" : ""}
              </Badge>
            </Group>
          </Group>
          <Group gap="xs">
            <Text fw={500} color="dimmed" style={{ minWidth: 80 }}>
              Client:
            </Text>
            <Text>{client?.name || "N/A"}</Text>
          </Group>
          <Group gap="xs">
            <Text fw={500} color="dimmed" style={{ minWidth: 80 }}>
              ID:
            </Text>
            <Text>{client?.id || "N/A"}</Text>
          </Group>
        </Card>
      </Stack>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        companyCode={code}
      />
    </PageContent>
  );
};

export default CustomerSupportPage;
